import { ReactNode } from 'react'
import classes from './ChatWidget.module.scss'

/**
 * Bot balonunun düz metnini okunabilir bloklara çevirir. Sunucu metni DEĞİŞTİRİLMEZ, yalnız
 * biçimlendirilir: kalın, madde işareti, bölüm başlığı, "Etiket: değer" satırı ve tıklanabilir
 * bağlantı/e-posta/telefon. Bir kalıp tanınmazsa satır olduğu gibi basılır — veri kaybı riski yok.
 *
 * Kurumsal iletişim metinleri (RAG korpusu) sitelerden kazındığı için etiketi ve değeri AYRI
 * satırlara koyabiliyor ("Adres" \n "Ayazağa Mahallesi …"). Burada o çiftler tek satırda birleşir.
 */

/** Tıklanabilir hâle getirilecek tek şema kümesi. Dışındaki değer düz metin kalır. */
const SAFE_SCHEME = /^(?:https?:\/\/|mailto:|tel:)/i

/**
 * Tek geçişte satır içi jeton yakalar. Gruplar: 1-2 markdown bağlantı, 3 kalın, 4 çıplak adres,
 * 5 e-posta, 6 telefon. Telefon YALNIZ telefon etiketli satırlarda bağlantıya çevrilir (aşağıda);
 * diğer satırlarda jeton olarak yakalanır ama düz metin basılır.
 */
const INLINE_PATTERN =
  /\[([^\]\n]+)\]\(([^\s)]+)\)|\*\*([^*\n]+)\*\*|((?:https?:\/\/|www\.)[^\s<>"']+)|([A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+)|(0[\d\s()]{9,18}\d)/g

/** Bağlantı/e-posta jetonunun sonuna yapışan cümle noktalaması adrese değil metne aittir. */
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/

/**
 * "Etiket: değer" ve yalnız-etiket satırlarını tanıyan anahtar kelimeler. Kasıtlı olarak dar:
 * listede olmayan bir "şey: şey" satırı düz metin kalır, böylece sıradan cümleler bozulmaz.
 */
const LABEL_KEYWORDS = /(adres|telefon|faks|fax|e-?posta|e-?mail|santral|çağrı merkezi|çalışma saat|konum|web sitesi)/i

/** Değerin `tel:` bağlantısına çevrileceği etiketler. Adres/e-posta satırında telefon aranmaz. */
const PHONE_LABEL = /(telefon|faks|fax|santral|çağrı merkezi)/i

/** Etiket adayının üst sınırı; uzun bir cümlenin içindeki iki nokta etiket sanılmasın. */
const MAX_LABEL_LENGTH = 40

/** Ayrı satırdaki değerin üst sınırı. Adresler ~120 karakter; düzyazı paragrafları bunu aşar. */
const MAX_DETACHED_VALUE_LENGTH = 150

/** Başlık adayının üst sınırı (ör. "INDEX AŞ / Merkez Ofis"). */
const MAX_HEADING_LENGTH = 60

/** Sadece telefon numarası taşıyan satır — başlığa terfi ETTİRİLMEZ, bir önceki etiketin değeridir. */
const PHONE_ONLY_LINE = /^0[\d\s()]{9,18}\d$/

/** Sadece e-posta taşıyan satır — aynı gerekçeyle başlığa terfi ettirilmez. */
const MAIL_ONLY_LINE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/

/** "•", "* " ve "- " madde işareti sayılır; tek "*" (çarpım) ve tire içeren metin sayılmaz. */
const BULLET_PREFIX = /^(?:•|\*\s|-\s)\s*/

/** Şirket/ofis başlığı kalıbı ("TEKLOS AŞ / Lojistik Merkezi"). */
const SLASH_HEADING = /\s\/\s/

/** Türkiye numarası: başında 0, toplam 11 hane. Sipariş/IBAN gibi hane dizileri elenir. */
function telHref(raw: string): string | null {
  const digits = raw.replace(/\D/g, '')
  if (digits.length !== 11 || !digits.startsWith('0')) return null
  return `tel:+90${digits.slice(1)}`
}

/** Güvenli adres üretir; şema tanınmazsa null döner ve jeton düz metin basılır. */
function safeHref(raw: string): string | null {
  const url = raw.trim()
  if (SAFE_SCHEME.test(url)) return url
  if (/^www\./i.test(url)) return `https://${url}`
  return null
}

function link(href: string, text: string, key: string) {
  return (
    <a key={key} className={classes.richLink} href={href} target='_blank' rel='noopener noreferrer'>
      {text}
    </a>
  )
}

/**
 * Satır içini parçalara ayırır. `phoneContext` yalnız telefon etiketli satırlarda true'dur;
 * false iken yakalanan numara jetonu düz metin olarak basılır (adres içindeki hane dizileri
 * yanlışlıkla aranabilir bağlantıya dönüşmesin).
 */
export function renderInline(line: string, keyPrefix: string, phoneContext = false): ReactNode[] {
  const nodes: ReactNode[] = []
  let cursor = 0
  let index = 0

  INLINE_PATTERN.lastIndex = 0
  for (let match = INLINE_PATTERN.exec(line); match !== null; match = INLINE_PATTERN.exec(line)) {
    if (match.index > cursor) nodes.push(line.slice(cursor, match.index))
    cursor = match.index + match[0].length

    const key = `${keyPrefix}-${index++}`
    const [, mdText, mdUrl, bold, url, mail, phone] = match

    if (mdUrl !== undefined) {
      const href = safeHref(mdUrl)
      nodes.push(href ? link(href, mdText, key) : match[0])
      continue
    }
    if (bold !== undefined) {
      nodes.push(<strong key={key}>{bold}</strong>)
      continue
    }
    if (url !== undefined || mail !== undefined) {
      const token = (url ?? mail) as string
      const trailing = token.match(TRAILING_PUNCTUATION)?.[0] ?? ''
      const clean = trailing ? token.slice(0, -trailing.length) : token
      const href = url !== undefined ? safeHref(clean) : `mailto:${clean}`
      nodes.push(href ? link(href, clean, key) : clean)
      if (trailing) nodes.push(trailing)
      continue
    }
    if (phone !== undefined) {
      const href = phoneContext ? telHref(phone) : null
      nodes.push(href ? link(href, phone, key) : phone)
      continue
    }
  }

  if (cursor < line.length) nodes.push(line.slice(cursor))
  return nodes
}

type LineKind = 'bullet' | 'labelValue' | 'labelOnly' | 'heading' | 'plain'

interface ParsedLine {
  kind: LineKind
  /** labelValue satırında etiket; diğerlerinde boş. */
  label: string
  /** Basılacak metin (labelValue'da değer, diğerlerinde satırın kendisi). */
  content: string
  /** Değerde telefon numarası aranacak mı (etiket telefon/faks/santral ise). */
  phone: boolean
}

function isLabelOnly(line: string): boolean {
  return !line.includes(':') && line.length <= MAX_LABEL_LENGTH && LABEL_KEYWORDS.test(line)
}

/** Ayrı satırdaki değer adayı: kısa, cümle noktalamasıyla bitmeyen, kendisi etiket olmayan satır. */
function isDetachedValue(line: string): boolean {
  if (!line || line.length > MAX_DETACHED_VALUE_LENGTH) return false
  if (BULLET_PREFIX.test(line) || isLabelOnly(line)) return false
  return !/[.?!]$/.test(line)
}

/** Sondaki noktası düşürülen kısa bilgi etiketleri: adres, çalışma saatleri, konum. */
const DOT_TRIM_LABEL = /(adres|çalışma saat|konum)/i

/**
 * Telefon/faks/e-posta/adres/çalışma saati/konum değerinin sonundaki cümle noktası içerik hatasıdır;
 * basılırken düşer. Yalnız SONDAKİ nokta: adresin içindeki kısaltmalar ("Cad.", "Mah.") korunur.
 */
function trimValueDot(value: string, label: string): string {
  const trimmed = value.replace(/\.$/, '')
  return PHONE_LABEL.test(label) || DOT_TRIM_LABEL.test(label) || MAIL_ONLY_LINE.test(trimmed) ? trimmed : value
}

/** Her satırı bağımsız sınıflar; başlık terfisi ve etiket-değer birleştirmesi sonraki geçişlerde. */
function classify(line: string): ParsedLine {
  if (BULLET_PREFIX.test(line)) {
    return { kind: 'bullet', label: '', content: line.replace(BULLET_PREFIX, ''), phone: false }
  }

  const colon = line.indexOf(':')
  if (colon > 0) {
    const label = line.slice(0, colon).trim()
    const value = line.slice(colon + 1).trim()
    if (value && label.length <= MAX_LABEL_LENGTH && LABEL_KEYWORDS.test(label)) {
      const phone = PHONE_LABEL.test(label)
      return { kind: 'labelValue', label, content: trimValueDot(value, label), phone }
    }
  }

  if (isLabelOnly(line)) return { kind: 'labelOnly', label: '', content: line, phone: false }
  return { kind: 'plain', label: '', content: line, phone: false }
}

/** Metni basılabilir satırlara çevirir. Boş satırlar blok ayırıcıdır ve çıktıya alınmaz. */
export function parseBotText(text: string): ParsedLine[] {
  const raw = text.split('\n').map(line => line.trim())
  const parsed = raw.map(line => (line ? classify(line) : null))

  // Geriye doğru: bir sonraki dolu satır etiket ya da başlıksa, kısa ve iki noktasız satır
  // bölüm başlığıdır ("INDEX AŞ / Merkez Ofis", "Merkez Ofis."). Salt telefon/e-posta satırı
  // bir önceki etiketin değeridir — terfi ettirilmez.
  for (let i = parsed.length - 1; i >= 0; i -= 1) {
    const line = parsed[i]
    if (!line || line.kind !== 'plain') continue
    if (line.content.includes(':') || line.content.length > MAX_HEADING_LENGTH) continue
    if (PHONE_ONLY_LINE.test(line.content) || MAIL_ONLY_LINE.test(line.content)) continue

    const next = parsed.slice(i + 1).find(Boolean)
    const nextIsAnchor = next
      ? next.kind === 'labelOnly' || next.kind === 'labelValue' || next.kind === 'heading'
      : false
    if (SLASH_HEADING.test(line.content) || nextIsAnchor) {
      line.kind = 'heading'
      line.content = line.content.replace(/\.$/, '')
    }
  }

  // İleriye doğru: yalnız-etiket satırını takip eden değer satırıyla birleştir. Değer yoksa
  // (ör. "Telefon Numaraları" → "Santral") etiket bir grup başlığıdır.
  const result: ParsedLine[] = []
  for (let i = 0; i < parsed.length; i += 1) {
    const line = parsed[i]
    if (!line) continue

    if (line.kind === 'labelOnly') {
      const valueIndex = parsed.findIndex((candidate, j) => j > i && candidate !== null)
      const value = valueIndex > i ? parsed[valueIndex] : null
      const label = line.content.replace(/[:.]$/, '')
      // Adres/çalışma saati/konum değeri sondaki noktası yüzünden "cümle" sayılıp etiketinden kopmasın.
      const detached = value ? trimValueDot(value.content, label) : ''
      if (value && value.kind === 'plain' && isDetachedValue(detached)) {
        const phone = PHONE_LABEL.test(label)
        result.push({ kind: 'labelValue', label, content: detached, phone })
        parsed[valueIndex] = null
        continue
      }
      result.push({ kind: 'heading', label: '', content: line.content.replace(/[:.]$/, ''), phone: false })
      continue
    }

    result.push(line)
  }

  // Etiketsiz kalan telefon satırı (ör. çağrı merkezinin ikinci numarası) bir önceki telefon
  // etiketinin devamıdır: aranabilir kalması için o bağlamı devralır ve boş etiketle değer
  // sütununa hizalanır — yoksa blok kenarında başıboş durur.
  for (let i = 1; i < result.length; i += 1) {
    if (result[i].kind === 'plain' && PHONE_ONLY_LINE.test(result[i].content) && result[i - 1].phone) {
      result[i] = { kind: 'labelValue', label: '', content: result[i].content, phone: true }
    }
  }

  return result
}

/** Bot metnini biçimlendirilmiş bloklara çevirir. */
export default function renderBotText(text: string): ReactNode {
  const lines = parseBotText(text)
  const hasList = lines.some(line => line.kind === 'bullet')

  return lines.map((line, i) => {
    if (line.kind === 'bullet') {
      return (
        <div key={i} className={classes.listItem}>
          <span className={classes.listDot} />
          <span className={classes.listText}>{renderInline(line.content, `l${i}`)}</span>
        </div>
      )
    }

    if (line.kind === 'heading') {
      return (
        <div key={i} className={`${classes.msgTitle} ${classes.richHeading}`}>
          {renderInline(line.content, `h${i}`)}
        </div>
      )
    }

    if (line.kind === 'labelValue') {
      return (
        <div key={i} className={classes.richField}>
          <span className={classes.richLabel}>{line.label}</span>
          <span className={classes.richValue}>{renderInline(line.content, `v${i}`, line.phone)}</span>
        </div>
      )
    }

    // Liste varken ilk satır giriş başlığıdır (mevcut davranış korunuyor).
    if (hasList && i === 0) {
      return (
        <div key={i} className={classes.msgTitle}>
          {line.content}
        </div>
      )
    }

    return <div key={i}>{renderInline(line.content, `p${i}`, line.phone)}</div>
  })
}
